import { useCallback, useEffect, useRef, useState } from 'react';
import { ChunkedRecorder, RecordedChunk } from '../utils/media/ChunkedRecorder';

export interface UseScreenRecorderOptions {
    /** Sequence number of the first chunk. Defaults to 1. */
    startIndex?: number;
    /** Overrides the default `screen_record_part_<index>.<extension>` file name. */
    getFileName?: (index: number, extension: string) => string;
    /** Called once per finished chunk. Awaited, so it can perform the upload. */
    onChunk?: (chunk: RecordedChunk) => void | Promise<void>;
}

/**
 * Records the shared screen as a sequence of uploadable files.
 *
 * `unavailable` is the signal the pre-exam screen uses to offer a retry. It is only
 * raised when sharing genuinely cannot continue — the browser refused the request, or
 * the candidate ended the share from the browser's own bar — never when the exam
 * closes a chunk itself.
 */
export const useScreenRecorder = (options: UseScreenRecorderOptions = {}) => {
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const streamRef = useRef<MediaStream | null>(null);
    const recorderRef = useRef<ChunkedRecorder | null>(null);
    const [recording, setRecording] = useState(false);
    const [unavailable, setUnavailable] = useState(false);

    if (!recorderRef.current) {
        recorderRef.current = new ChunkedRecorder({
            startIndex: options.startIndex,
            getFileName: (index, extension) =>
                optionsRef.current.getFileName?.(index, extension) ??
                `screen_record_part_${index}.${extension}`,
            onChunk: chunk => optionsRef.current.onChunk?.(chunk),
            onError: () => setUnavailable(true),
        });
    }

    const releaseStream = useCallback(() => {
        streamRef.current?.getTracks().forEach(track => {
            // Detach first: tearing the stream down ourselves is not a lost share.
            track.onended = null;
            track.stop();
        });
        streamRef.current = null;
    }, []);

    /** The candidate ended the share from the browser's own "stop sharing" control. */
    const handleShareEnded = useCallback(() => {
        setRecording(false);
        setUnavailable(true);
        // Keep whatever was captured before the share ended, then drop the dead stream.
        void recorderRef.current?.stop().finally(releaseStream);
    }, [releaseStream]);

    const startRecording = useCallback(async () => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.recording) return;

        try {
            const stream = streamRef.current
                ?? await navigator.mediaDevices.getDisplayMedia({ video: true });

            stream.getVideoTracks().forEach(track => { track.onended = handleShareEnded; });
            streamRef.current = stream;

            recorder.start(stream);
            setRecording(true);
            setUnavailable(false);
        } catch {
            releaseStream();
            setRecording(false);
            setUnavailable(true);
        }
    }, [handleShareEnded, releaseStream]);

    /** Closes the current chunk and keeps recording into the next one, without a gap. */
    const nextChunk = useCallback(() => {
        recorderRef.current?.rotate();
    }, []);

    /** Declares the chunk number covering now — aligns numbering with a resumed exam. */
    const setChunkIndex = useCallback((index: number) => {
        recorderRef.current?.setIndex(index);
    }, []);

    /** Stops recording and ends the share. Resolves once the final chunk is handled. */
    const stopRecording = useCallback(async () => {
        setRecording(false);
        await recorderRef.current?.stop();
        releaseStream();
    }, [releaseStream]);

    useEffect(() => () => {
        recorderRef.current?.cancel();
        releaseStream();
    }, [releaseStream]);

    return {
        recording,
        unavailable,
        nextChunk,
        startRecording,
        stopRecording,
        setChunkIndex,
    };
};
